/**
 * The voice signaling framing (web-plan.md F4): the same binary protobuf
 * envelopes every native client speaks on `/v1/ws`, via the buf-generated
 * schemas. One WS binary frame = one envelope; the DataChannel speaks the
 * media-plane `ControlMessage` the same way (one SCTP message = one body,
 * no transport AEAD — DTLS already owns that wire).
 */
import { create, fromBinary, toBinary, type MessageInitShape } from "@bufbuild/protobuf";

import {
  ClientMessageSchema,
  type ServerMessage,
  ServerMessageSchema,
} from "../../gen/proto/echo/signal/v1/signal_pb";
import {
  type ControlMessage,
  ControlMessageSchema,
} from "../../gen/proto/echo/media/v1/media_pb";

/** wire::PROTO_VER — a mismatch is Error + close, so it lives in one place. */
export const PROTO_VER = 0;
/** `echo_wire::caps` bit 0: this build can watch a screen share (D91).
 * Advertised only when WebCodecs H264 is actually there — the picker warns
 * a sharer about anyone whose bit is off, and a false promise is the one
 * thing that bit must never make. */
export const CAP_VIEW_SCREEN_SHARE = 1n;
/** `echo_wire::caps` bit 1: we send ChannelReady when the call is really up. */
export const CAP_ANNOUNCE_ON_READY = 2n;
// No `echo_wire::caps::CAMERA` (bit 3), on purpose: it says a build draws
// camera tiles (camera-plan.md D702), and the browser only marks the person
// whose camera is on (D703).

export type SMsg = ServerMessage["msg"];
export type CMsgInit = MessageInitShape<typeof ClientMessageSchema>["msg"];
export type ControlInit = MessageInitShape<typeof ControlMessageSchema>["msg"];

export function encodeClient(msg: CMsgInit): Uint8Array<ArrayBuffer> {
  // toBinary allocates a plain ArrayBuffer; the cast only says so.
  return toBinary(ClientMessageSchema, create(ClientMessageSchema, { msg })) as Uint8Array<ArrayBuffer>;
}

export function decodeServer(bytes: ArrayBuffer): SMsg {
  return fromBinary(ServerMessageSchema, new Uint8Array(bytes)).msg;
}

export function encodeControl(msg: ControlInit): Uint8Array<ArrayBuffer> {
  return toBinary(ControlMessageSchema, create(ControlMessageSchema, { msg })) as Uint8Array<ArrayBuffer>;
}

export function decodeControl(bytes: ArrayBuffer): ControlMessage["msg"] {
  return fromBinary(ControlMessageSchema, new Uint8Array(bytes)).msg;
}
